import { useEffect, useRef, useState } from 'react';

export const APP_SHELL_MIN_DISPLAY_MS = 1000;

/**
 * Keeps a loading indicator visible for at least `minMs` once loading has started,
 * preventing a flash when the underlying operation completes very quickly.
 */
export function useMinimumLoadingDuration(
  isLoading: boolean,
  minMs: number = APP_SHELL_MIN_DISPLAY_MS
): boolean {
  const [showLoading, setShowLoading] = useState(isLoading);
  const startedAtRef = useRef<number | null>(isLoading ? Date.now() : null);

  useEffect(() => {
    if (isLoading) {
      startedAtRef.current = Date.now();
      setShowLoading(true);
      return;
    }

    if (startedAtRef.current === null) {
      setShowLoading(false);
      return;
    }

    const elapsed = Date.now() - startedAtRef.current;
    const remaining = minMs - elapsed;

    if (remaining <= 0) {
      startedAtRef.current = null;
      setShowLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      startedAtRef.current = null;
      setShowLoading(false);
    }, remaining);

    return () => clearTimeout(timer);
  }, [isLoading, minMs]);

  return showLoading;
}
